// The geometry of the mark that names an armed pick mode (R469,
// ui-map-interface.md §8 and §6.3).
//
// The mark is a "+" with the middle missing: four arms on the two axes, with
// a gap between them. The gap keeps the mark off the point it names, so the
// player reads the water under the target rather than a line over it.
//
// Nothing here strokes anything, so the shape is testable without a renderer
// and a second renderer draws the same mark from the same numbers, the way
// `marks.ts` keeps its own shapes.

// The whole mark rides presentation.toml: the four figures of the mission
// mark, the aim mark and its ring, and the snap ring (R469). The colours are
// the order colours the mission dashes of `fleets.ts` draw with, so an armed
// order and the line it draws cannot disagree.
import { FLEET_CROSSHAIR, FLEET_ORDER_COLORS } from '../gen/presentation';

/**
 * The armed order, as this layer names it. `null` is "nothing is armed".
 *
 * `move` serves the MOVE button and the GO… pick, because both
 * place a destination and neither hunts on the way.
 */
export type CrosshairMode = 'move' | 'attack' | 'gather' | 'escort' | 'patrol' | 'aim' | null;

/**
 * The order colour each mode wears. `aim` wears the attack red: a shot and
 * an attack promise the same thing, and the map states that promise in one
 * colour.
 */
const MODE_COLOR: Readonly<Record<Exclude<CrosshairMode, null>, number>> = {
  move: FLEET_ORDER_COLORS.move.color,
  attack: FLEET_ORDER_COLORS.attack.color,
  gather: FLEET_ORDER_COLORS.gather.color,
  escort: FLEET_ORDER_COLORS.escort.color,
  patrol: FLEET_ORDER_COLORS.patrol.color,
  aim: FLEET_ORDER_COLORS.attack.color,
};

/** One drawn segment of the mark: from (x0, y0) to (x1, y1). */
export type CrosshairArm = readonly [number, number, number, number];

/** The whole mark for one mode, as geometry and nothing else. */
export interface CrosshairPaths {
  /** The four arms, in +x, -x, +y, -y order. */
  arms: CrosshairArm[];
  /** The radius of the aim circle, or 0 when the mode draws none. */
  ringR: number;
  /** The radius of the snap ring, or 0 when the point took nothing. */
  snapR: number;
  color: number;
}

/**
 * The mark for an armed order at a screen point, as pure geometry (R469).
 *
 * The layer below strokes what this returns. The split keeps the shape
 * testable without a renderer, the way `marks.ts` keeps its own shapes.
 *
 * `snapped` says the order point took a structure instead of the pixel under
 * the cursor. The mark then wears its snap ring, so the player sees the snap
 * and does not read the order as a miss. The aim never snaps, so the aim mode
 * ignores the flag.
 */
export function crosshairPaths(
  mode: Exclude<CrosshairMode, null>, x: number, y: number, snapped: boolean,
): CrosshairPaths {
  const c = FLEET_CROSSHAIR;
  const aim = mode === 'aim';
  const gap = aim ? c.aimGapPx : c.gapPx;
  const arm = aim ? c.aimArmPx : c.armPx;
  return {
    // Each arm runs from the gap out to the arm length, so the middle stays
    // empty. The aim arms reach past the ring and cross it, because
    // `aimArmPx` is over `aimRingR`: an arm that stopped at the ring read as
    // a move order in a bubble, and a shot must read as a gunsight. The
    // generator refuses a set that does not cross.
    arms: [
      [x + gap, y, x + arm, y],
      [x - gap, y, x - arm, y],
      [x, y + gap, x, y + arm],
      [x, y - gap, x, y - arm],
    ],
    ringR: aim ? c.aimRingR : 0,
    snapR: !aim && snapped ? c.snapRingR : 0,
    color: MODE_COLOR[mode],
  };
}

/** The marks at the origin, built once a (mode, snapped) pair. */
const AT_ORIGIN = new Map<Exclude<CrosshairMode, null>, CrosshairPaths>();
const AT_ORIGIN_SNAPPED = new Map<Exclude<CrosshairMode, null>, CrosshairPaths>();

/**
 * `crosshairPaths(mode, 0, 0, snapped)`, built once and kept (§10.3). A
 * layer asks for the shape around the origin and moves it into place, so
 * the answer depends on the mode and the snap alone. The caller must not
 * change what it reads.
 */
export function crosshairAtOrigin(
  mode: Exclude<CrosshairMode, null>, snapped: boolean,
): CrosshairPaths {
  const table = snapped ? AT_ORIGIN_SNAPPED : AT_ORIGIN;
  let path = table.get(mode);
  if (path === undefined) {
    path = crosshairPaths(mode, 0, 0, snapped);
    table.set(mode, path);
  }
  return path;
}
